import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import {
  ArrowLeft, Plus, Trash2, GripVertical, Copy, ListChecks, Eye, Loader2, Download, Upload, Send,
  Search, X, Columns3, ArrowUp, ArrowDown, ArrowUpDown, BarChart3, PieChart, Share2,
} from 'lucide-react';
import {
  DndContext, PointerSensor, useSensor, useSensors, closestCenter,
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext, useSortable, arrayMove, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { formularioService, formularioCampoService, formularioRespostaService } from '@/services/formulariosApi';
import { FormularioPreview } from '@/components/formularios/FormularioPreview';
import {
  Formulario, FormularioCampo, FormularioCampoTipo, FormularioStatus, FormularioResposta, FormularioDashboard, FormularioDashboardCampo,
} from '@/types';
import { useAuth } from '@/contexts/AuthContext';

const TIPOS_CAMPO: { value: FormularioCampoTipo; label: string }[] = [
  { value: 'texto_curto', label: 'Texto curto' },
  { value: 'texto_longo', label: 'Texto longo (parágrafo)' },
  { value: 'numero', label: 'Número' },
  { value: 'lista', label: 'Lista (dropdown)' },
  { value: 'selecao_unica', label: 'Seleção única' },
  { value: 'multipla_escolha', label: 'Múltipla escolha' },
  { value: 'data', label: 'Data' },
  { value: 'hora', label: 'Hora' },
  { value: 'data_hora', label: 'Data e hora' },
  { value: 'escala', label: 'Escala / avaliação' },
  { value: 'imagem', label: 'Imagem' },
  { value: 'arquivo', label: 'Arquivo' },
  { value: 'assinatura', label: 'Assinatura eletrônica' },
];
const TIPOS_CAMPO_VALORES = TIPOS_CAMPO.map((t) => t.value);

const TIPOS_COM_OPCOES: FormularioCampoTipo[] = ['lista', 'selecao_unica', 'multipla_escolha'];
// A página pública (FormularioPublico.tsx) desabilita esses tipos ("ainda não disponível pelo
// link público") — se um deles for obrigatório com o link ativo, ninguém consegue enviar pelo
// link (revisão da PR #89, item 7). Bloqueado nos dois pontos de entrada: marcar obrigatório
// com link já ativo, e ativar o link com um desses já obrigatório.
const TIPOS_INDISPONIVEIS_LINK_PUBLICO: FormularioCampoTipo[] = ['imagem', 'arquivo', 'assinatura'];
// "Outro" (resposta personalizada) só faz sentido em seleção única/múltipla — numa lista dropdown
// já dá pra digitar direto, e nos outros tipos não existem "opções" pra completar.
const TIPOS_COM_OUTRO: FormularioCampoTipo[] = ['selecao_unica', 'multipla_escolha'];

const STATUS_CONFIG: Record<FormularioStatus, { label: string; className: string }> = {
  rascunho: { label: 'Rascunho', className: 'bg-slate-200 text-slate-700' },
  publicado: { label: 'Publicado', className: 'bg-emerald-200 text-emerald-800' },
  arquivado: { label: 'Arquivado', className: 'bg-amber-200 text-amber-800' },
};

const CSV_COLUNAS = ['tipo', 'label', 'obrigatorio', 'opcoes', 'permite_outro', 'escala_min', 'escala_max'];

function formatarValor(valor: unknown): string {
  if (valor === null || valor === undefined || valor === '') return '—';
  if (Array.isArray(valor)) return valor.map((v) => formatarValor(v)).join(', ');
  if (typeof valor === 'object') {
    const obj = valor as Record<string, unknown>;
    if (obj.outro) return `Outro: ${obj.texto || ''}`;
    if (obj.nome) return String(obj.nome);
    return JSON.stringify(obj);
  }
  if (valor === 'sim') return 'Sim';
  if (valor === 'nao') return 'Não';
  return String(valor);
}

// Tipos com um conjunto fixo de respostas possíveis — só esses fazem sentido como filtro
// "responda igual a X" (texto livre/número/data não têm opções pra escolher).
const TIPOS_FILTRAVEIS: FormularioCampoTipo[] = ['lista', 'selecao_unica', 'multipla_escolha', 'escala'];

function opcoesParaFiltro(campo: FormularioCampo): string[] {
  const cfg = (campo.configuracao || {}) as Record<string, unknown>;
  if (campo.tipo === 'escala') {
    const min = typeof cfg.min === 'number' ? cfg.min : 1;
    const max = typeof cfg.max === 'number' ? cfg.max : 5;
    return Array.from({ length: max - min + 1 }, (_, i) => String(min + i));
  }
  const opcoes = Array.isArray(cfg.opcoes) ? (cfg.opcoes as string[]) : [];
  return cfg.permiteOutro ? [...opcoes, 'Outro'] : opcoes;
}

const LABELS_NEGATIVOS = ['não', 'nao', 'ñok', 'nok', 'ruim', 'insatisfeito', 'insatisfatório'];

// Prioriza no dashboard as perguntas com mais respostas pendentes ou com maior proporção de
// respostas "negativas" (Não/ÑOK/etc.) — quanto maior o score, mais no topo aparece.
function criticidadeCampo(c: FormularioDashboardCampo, totalRespostas: number): number {
  const taxaPendente = totalRespostas > 0 ? 1 - c.respondidas / totalRespostas : 0;
  let taxaNegativa = 0;
  if (c.distribuicao && c.distribuicao.length > 0 && c.respondidas > 0) {
    const negativos = c.distribuicao
      .filter((d) => LABELS_NEGATIVOS.some((neg) => d.opcao.toLowerCase().includes(neg)))
      .reduce((soma, d) => soma + d.count, 0);
    taxaNegativa = negativos / c.respondidas;
  }
  return taxaPendente + taxaNegativa;
}

const CORES_GRAFICO = ['#7c6df2', '#e8b339', '#22c55e', '#ef4444', '#06b6d4', '#a855f7', '#f97316', '#64748b'];

function DonutDistribuicao({ dados }: { dados: { opcao: string; count: number }[] }) {
  const total = dados.reduce((soma, d) => soma + d.count, 0) || 1;
  let acumulado = 0;
  const fatias = dados.map((d, i) => {
    const inicio = (acumulado / total) * 360;
    acumulado += d.count;
    const fim = (acumulado / total) * 360;
    return `${CORES_GRAFICO[i % CORES_GRAFICO.length]} ${inicio}deg ${fim}deg`;
  });
  return (
    <div className="flex items-center gap-3">
      <div
        className="h-20 w-20 rounded-full shrink-0"
        style={{ background: `conic-gradient(${fatias.join(', ')})` }}
      />
      <div className="space-y-1 min-w-0 flex-1">
        {dados.map((d, i) => (
          <div key={d.opcao} className="flex items-center gap-1.5 text-xs">
            <span className="h-2 w-2 rounded-full shrink-0" style={{ background: CORES_GRAFICO[i % CORES_GRAFICO.length] }} />
            <span className="truncate">{d.opcao}</span>
            <span className="text-muted-foreground ml-auto whitespace-nowrap">
              {d.count} ({Math.round((d.count / total) * 100)}%)
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Compara o valor bruto salvo (string, número, array, ou {outro,texto}) com a opção escolhida no
// filtro (que já vem "formatada" — ex: "Sim", "Outro") — reaproveita formatarValor pra cada item.
function valorContemOpcao(valorBruto: unknown, alvo: string): boolean {
  if (Array.isArray(valorBruto)) return valorBruto.some((item) => formatarValor(item) === alvo);
  return formatarValor(valorBruto) === alvo;
}

function SortableHeader({
  coluna, ordenacao, onClick, children,
}: {
  coluna: string;
  ordenacao: { coluna: string; dir: 'asc' | 'desc' } | null;
  onClick: (coluna: string) => void;
  children: ReactNode;
}) {
  const ativo = ordenacao?.coluna === coluna;
  const Icone = ativo ? (ordenacao!.dir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <button
      type="button"
      onClick={() => onClick(coluna)}
      className={`flex items-center gap-1 hover:text-foreground ${ativo ? 'text-foreground font-medium' : ''}`}
    >
      {children}
      <Icone className="h-3 w-3 shrink-0" />
    </button>
  );
}

// ─── Linha de campo — tudo editável direto, sem popup ───────────────────────

function CampoRow({
  campo, isAdmin, precisaEscolherTipo, semTexto, onUpdate, onEscolherTipo, onDelete, onDuplicar,
}: {
  campo: FormularioCampo;
  isAdmin: boolean;
  precisaEscolherTipo: boolean;
  semTexto: boolean;
  onUpdate: (patch: Partial<Pick<FormularioCampo, 'tipo' | 'label' | 'obrigatorio' | 'configuracao'>>) => void;
  onEscolherTipo: (tipo: FormularioCampoTipo) => void;
  onDelete: (c: FormularioCampo) => void;
  onDuplicar: (c: FormularioCampo) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: campo.id });
  const style: CSSProperties = { transform: CSS.Transform.toString(transform), transition };
  const cfg = (campo.configuracao || {}) as Record<string, unknown>;

  const [label, setLabel] = useState(campo.label);
  const [opcoesTexto, setOpcoesTexto] = useState(
    Array.isArray(cfg.opcoes) ? (cfg.opcoes as string[]).join('\n') : ''
  );
  // Guardados como texto (não number) enquanto o usuário digita, senão o campo vazio
  // vira `0` sozinho e todo dígito seguinte é anexado a ele (ex: "010").
  const [escalaMinTexto, setEscalaMinTexto] = useState(String(typeof cfg.min === 'number' ? cfg.min : 1));
  const [escalaMaxTexto, setEscalaMaxTexto] = useState(String(typeof cfg.max === 'number' ? cfg.max : 5));
  const permiteOutro = cfg.permiteOutro === true;

  const commitLabel = () => {
    const trimmed = label.trim();
    if (trimmed && trimmed !== campo.label) onUpdate({ label: trimmed });
    else if (!trimmed) setLabel(campo.label);
  };

  const commitOpcoes = () => {
    const opcoes = opcoesTexto.split('\n').map((o) => o.trim()).filter(Boolean);
    onUpdate({ configuracao: { ...cfg, opcoes } });
  };

  const commitPermiteOutro = (v: boolean) => {
    onUpdate({ configuracao: { ...cfg, permiteOutro: v } });
  };

  const commitEscala = () => {
    const min = parseInt(escalaMinTexto, 10);
    const max = parseInt(escalaMaxTexto, 10);
    const minValido = Number.isNaN(min) ? 1 : min;
    const maxValido = Number.isNaN(max) ? 5 : max;
    setEscalaMinTexto(String(minValido));
    setEscalaMaxTexto(String(maxValido));
    onUpdate({ configuracao: { ...cfg, min: minValido, max: maxValido } });
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`p-3 rounded-lg border bg-card space-y-3 ${isDragging ? 'opacity-40' : ''}`}
    >
      <div className="flex items-start gap-3">
        {isAdmin && (
          <button
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing p-1 mt-1.5 rounded hover:bg-muted touch-none shrink-0"
            title="Arrastar para reordenar"
            type="button"
          >
            <GripVertical className="h-4 w-4 text-muted-foreground" />
          </button>
        )}

        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex flex-col sm:flex-row gap-2">
            <Select
              value={precisaEscolherTipo ? undefined : campo.tipo}
              onValueChange={(v) => onEscolherTipo(v as FormularioCampoTipo)}
              disabled={!isAdmin}
            >
              <SelectTrigger className={`sm:w-[190px] shrink-0 h-9 text-sm text-left [&>span]:text-left ${precisaEscolherTipo ? 'text-muted-foreground border-primary/50' : ''}`}>
                <SelectValue placeholder="Escolha um tipo..." />
              </SelectTrigger>
              <SelectContent>
                {TIPOS_CAMPO.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="relative flex-1">
              <Input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                onBlur={commitLabel}
                disabled={!isAdmin || precisaEscolherTipo}
                placeholder="Rótulo (pergunta)"
                className={`h-9 pr-6 ${semTexto ? 'border-destructive focus-visible:ring-destructive' : ''}`}
              />
              {!precisaEscolherTipo && campo.obrigatorio && (
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-destructive font-medium" title="Campo obrigatório">*</span>
              )}
            </div>
          </div>
          {semTexto && (
            <p className="text-xs text-destructive">Escreva o texto da pergunta — sem ele ela não pode ser publicada.</p>
          )}

          {!precisaEscolherTipo && TIPOS_COM_OPCOES.includes(campo.tipo) && (
            <Textarea
              value={opcoesTexto}
              onChange={(e) => setOpcoesTexto(e.target.value)}
              onBlur={commitOpcoes}
              disabled={!isAdmin}
              rows={3}
              placeholder={'Uma opção por linha\nOpção 1\nOpção 2'}
              className="text-sm"
            />
          )}

          {!precisaEscolherTipo && campo.tipo === 'escala' && (
            <div className="flex items-center gap-2 text-sm">
              <Label className="text-muted-foreground">Mín</Label>
              <Input
                type="number"
                className="w-16 h-8"
                value={escalaMinTexto}
                disabled={!isAdmin}
                onChange={(e) => setEscalaMinTexto(e.target.value)}
                onBlur={commitEscala}
              />
              <Label className="text-muted-foreground">Máx</Label>
              <Input
                type="number"
                className="w-16 h-8"
                value={escalaMaxTexto}
                disabled={!isAdmin}
                onChange={(e) => setEscalaMaxTexto(e.target.value)}
                onBlur={commitEscala}
              />
            </div>
          )}

          {!precisaEscolherTipo && isAdmin && TIPOS_COM_OUTRO.includes(campo.tipo) && (
            <div className="flex items-center gap-2 text-sm pt-1">
              <Switch checked={permiteOutro} onCheckedChange={commitPermiteOutro} />
              <span className="text-muted-foreground">Permitir opção "Outro" (resposta personalizada)</span>
            </div>
          )}
        </div>

        {isAdmin && (
          <div className="flex items-center gap-2 shrink-0">
            {!precisaEscolherTipo && (
              <div className="flex items-center gap-1.5">
                <Switch
                  checked={campo.obrigatorio}
                  onCheckedChange={(v) => onUpdate({ obrigatorio: v })}
                />
                <span className="text-xs text-muted-foreground hidden lg:inline">Obrigatório</span>
              </div>
            )}
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onDuplicar(campo)} title="Duplicar">
              <Copy className="h-3.5 w-3.5" />
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => onDelete(campo)} title="Excluir">
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function GerenciarFormulario() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.tipo === 'admin' || user?.tipo === 'superadmin';

  const [formulario, setFormulario] = useState<Formulario | null>(null);
  const [loading, setLoading] = useState(true);
  const [salvandoConfig, setSalvandoConfig] = useState(false);
  const [excluirAlvo, setExcluirAlvo] = useState<FormularioCampo | null>(null);
  const [confirmarPublicarLink, setConfirmarPublicarLink] = useState(false);
  const [confirmarDesativarLink, setConfirmarDesativarLink] = useState(false);
  const [confirmarStatusPendente, setConfirmarStatusPendente] = useState<FormularioStatus | null>(null);
  const [camposInvalidos, setCamposInvalidos] = useState<Set<string>>(new Set());
  const [adicionandoCampo, setAdicionandoCampo] = useState(false);
  const [importandoCsv, setImportandoCsv] = useState(false);
  const [pendentesDeTipo, setPendentesDeTipo] = useState<Set<string>>(new Set());
  const [gerandoLink, setGerandoLink] = useState(false);
  const [respostas, setRespostas] = useState<FormularioResposta[] | null>(null);
  const [carregandoRespostas, setCarregandoRespostas] = useState(false);
  const [respostaDetalhe, setRespostaDetalhe] = useState<FormularioResposta | null>(null);
  const [dashboard, setDashboard] = useState<FormularioDashboard | null>(null);
  const [carregandoDashboard, setCarregandoDashboard] = useState(false);
  const [buscaResposta, setBuscaResposta] = useState('');
  const [filtroCampoId, setFiltroCampoId] = useState('');
  const [filtroValor, setFiltroValor] = useState('');
  const [colunasOcultas, setColunasOcultas] = useState<Set<string>>(new Set());
  const [ordenacaoResposta, setOrdenacaoResposta] = useState<{ coluna: string; dir: 'asc' | 'desc' } | null>(null);
  const [abaAtual, setAbaAtual] = useState('editor');
  const [periodoDashboard, setPeriodoDashboard] = useState<'7' | '30' | '90' | 'tudo'>('tudo');
  const [tipoGraficoPorCampo, setTipoGraficoPorCampo] = useState<Record<string, 'barra' | 'pizza'>>({});
  const csvInputRef = useRef<HTMLInputElement>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const load = async () => {
    if (!id) return;
    setLoading(true);
    const res = await formularioService.getById(id);
    if (res.success) setFormulario(res.data);
    else toast.error(res.error || 'Erro ao carregar formulário');
    setLoading(false);
  };

  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    if (!id) return;
    const raw = localStorage.getItem(`formulario-filtros-${id}`);
    if (!raw) return;
    try {
      const salvo = JSON.parse(raw);
      if (salvo.buscaResposta) setBuscaResposta(salvo.buscaResposta);
      if (salvo.filtroCampoId) setFiltroCampoId(salvo.filtroCampoId);
      if (salvo.filtroValor) setFiltroValor(salvo.filtroValor);
    } catch {
      // ignora filtros salvos corrompidos
    }
  }, [id]);

  useEffect(() => {
    if (!id) return;
    localStorage.setItem(
      `formulario-filtros-${id}`,
      JSON.stringify({ buscaResposta, filtroCampoId, filtroValor })
    );
  }, [id, buscaResposta, filtroCampoId, filtroValor]);

  const campos = formulario?.campos ? [...formulario.campos].sort((a, b) => a.ordem - b.ordem) : [];
  const camposFiltraveis = campos.filter((c) => TIPOS_FILTRAVEIS.includes(c.tipo));
  const campoDoFiltro = camposFiltraveis.find((c) => c.id === filtroCampoId) || null;
  const colunasRespostas = campos.filter((c) => !colunasOcultas.has(c.id));

  const [larguraColunas, setLarguraColunas] = useState<Record<string, number>>({});
  const resizeRef = useRef<{ colId: string; startX: number; startWidth: number } | null>(null);
  const larguraColuna = (colId: string, padrao: number) => larguraColunas[colId] ?? padrao;

  const iniciarRedimensionamento = (e: React.MouseEvent, colId: string, larguraAtual: number) => {
    e.preventDefault();
    e.stopPropagation();
    resizeRef.current = { colId, startX: e.clientX, startWidth: larguraAtual };
    const onMouseMove = (ev: MouseEvent) => {
      if (!resizeRef.current) return;
      const delta = ev.clientX - resizeRef.current.startX;
      const novaLargura = Math.max(80, resizeRef.current.startWidth + delta);
      setLarguraColunas((prev) => ({ ...prev, [resizeRef.current!.colId]: novaLargura }));
    };
    const onMouseUp = () => {
      resizeRef.current = null;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const valorOrdenavel = (r: FormularioResposta, coluna: string): string | number => {
    if (coluna === 'nome') return (r.respondente?.nome || r.respondente_nome || '').toLowerCase();
    if (coluna === 'email') return (r.respondente?.email || r.respondente_email || '').toLowerCase();
    if (coluna === 'data') return new Date(r.respondido_em).getTime();
    const valor = r.valores?.find((v) => v.campo_id === coluna)?.valor;
    const numero = Number(valor);
    if (typeof valor === 'number' || (typeof valor === 'string' && valor.trim() && !Number.isNaN(numero))) return numero;
    return formatarValor(valor).toLowerCase();
  };

  const toggleOrdenacaoResposta = (coluna: string) => {
    setOrdenacaoResposta((prev) => {
      if (prev?.coluna === coluna) return { coluna, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      return { coluna, dir: 'asc' };
    });
  };

  const respostasFiltradas = (respostas || [])
    .filter((r) => {
      const nome = (r.respondente?.nome || r.respondente_nome || '').toLowerCase();
      const email = (r.respondente?.email || r.respondente_email || '').toLowerCase();
      const busca = buscaResposta.trim().toLowerCase();
      if (busca && !nome.includes(busca) && !email.includes(busca)) return false;
      if (filtroCampoId && filtroValor) {
        const valor = r.valores?.find((v) => v.campo_id === filtroCampoId)?.valor;
        if (!valorContemOpcao(valor, filtroValor)) return false;
      }
      return true;
    })
    .sort((a, b) => {
      if (!ordenacaoResposta) return 0;
      const va = valorOrdenavel(a, ordenacaoResposta.coluna);
      const vb = valorOrdenavel(b, ordenacaoResposta.coluna);
      const cmp = typeof va === 'number' && typeof vb === 'number' ? va - vb : String(va).localeCompare(String(vb), 'pt-BR');
      return ordenacaoResposta.dir === 'asc' ? cmp : -cmp;
    });

  // Usado antes de publicar (troca de status pra "publicado") e antes de gerar link público —
  // as duas ações que tornam o formulário acessível a alguém de fora precisam da mesma garantia
  // de que toda pergunta tem texto.
  const validarPerguntasParaPublicar = (): boolean => {
    const invalidos = campos.filter((c) => !c.label.trim());
    if (invalidos.length > 0) {
      setCamposInvalidos(new Set(invalidos.map((c) => c.id)));
      toast.error('Escreva o texto da pergunta — sem ele ela não pode ser publicada.');
      return false;
    }
    setCamposInvalidos(new Set());
    return true;
  };

  const handleStatusChange = async (status: FormularioStatus) => {
    if (status === 'publicado') {
      if (!validarPerguntasParaPublicar()) return;
    } else {
      setCamposInvalidos(new Set());
    }
    if (status !== 'publicado' && formulario) {
      // Revalida o share_token direto do servidor — o state local pode estar desatualizado
      // (outra aba/outro admin pode ter gerado ou desativado o link nesse meio tempo).
      const fresh = await formularioService.getById(formulario.id);
      const shareTokenAtual = fresh.success ? fresh.data?.share_token : formulario.share_token;
      if (shareTokenAtual) {
        setConfirmarStatusPendente(status);
        return;
      }
    }
    atualizarFormulario({ status });
  };

  const handleConfirmarTrocarStatus = () => {
    if (!confirmarStatusPendente) return;
    const status = confirmarStatusPendente;
    setConfirmarStatusPendente(null);
    atualizarFormulario({ status });
  };

  const atualizarFormulario = async (updates: Partial<Formulario>) => {
    if (!formulario) return;
    setSalvandoConfig(true);
    const res = await formularioService.update(formulario.id, updates);
    setSalvandoConfig(false);
    if (res.success) {
      setFormulario((prev) => (prev ? { ...prev, ...res.data } : prev));
      if ((res as any).linkPublicoAfetado) {
        toast.warning('O link público desse formulário parou de funcionar enquanto o status não voltar para "Publicado".');
      }
    } else {
      toast.error(res.error || 'Erro ao salvar');
    }
  };

  const copiarLinkPublico = async (link: string) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(link);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = link;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      toast.success('Link copiado');
    } catch {
      toast.error('Não foi possível copiar o link automaticamente. Copie manualmente.');
    }
  };

  const handleAlternarLinkPublico = (ativar: boolean) => {
    if (!ativar) {
      setConfirmarDesativarLink(true);
      return;
    }
    if (formulario && formulario.status !== 'publicado') {
      setConfirmarPublicarLink(true);
      return;
    }
    handleGerarLink();
  };

  const handleConfirmarPublicarEGerarLink = () => {
    setConfirmarPublicarLink(false);
    handleGerarLink();
  };

  const handleConfirmarDesativarLink = () => {
    setConfirmarDesativarLink(false);
    handleDesativarLink();
  };

  const handleGerarLink = async () => {
    if (!formulario) return;
    if (!validarPerguntasParaPublicar()) return;
    const campoBloqueado = (formulario.campos || []).find(
      (c) => TIPOS_INDISPONIVEIS_LINK_PUBLICO.includes(c.tipo) && c.obrigatorio
    );
    if (campoBloqueado) {
      const tipoLabel = TIPOS_CAMPO.find((t) => t.value === campoBloqueado.tipo)?.label || campoBloqueado.tipo;
      toast.error(`O campo "${campoBloqueado.label}" (${tipoLabel}) é obrigatório e não está disponível no link público — torne-o opcional antes de gerar o link.`);
      return;
    }
    setGerandoLink(true);
    const res = await formularioService.gerarLink(formulario.id);
    setGerandoLink(false);
    if (res.success) {
      setFormulario((prev) => (prev ? { ...prev, share_token: res.data.share_token, status: res.data.status } : prev));
      toast.success('Link público gerado');
    } else {
      toast.error(res.error || 'Erro ao gerar link');
    }
  };

  const handleDesativarLink = async () => {
    if (!formulario) return;
    setGerandoLink(true);
    const res = await formularioService.desativarLink(formulario.id);
    setGerandoLink(false);
    if (res.success) {
      setFormulario((prev) => (prev ? { ...prev, share_token: null } : prev));
      toast.success('Link público desativado');
    } else {
      toast.error(res.error || 'Erro ao desativar link');
    }
  };

  const carregarRespostas = async () => {
    if (!formulario || respostas !== null) return;
    setCarregandoRespostas(true);
    const res = await formularioRespostaService.listar(formulario.id);
    setCarregandoRespostas(false);
    if (res.success) setRespostas(res.data);
    else toast.error(res.error || 'Erro ao carregar respostas');
  };

  const carregarDashboard = async () => {
    if (!formulario || dashboard !== null) return;
    setCarregandoDashboard(true);
    const res = await formularioRespostaService.dashboard(formulario.id);
    setCarregandoDashboard(false);
    if (res.success) setDashboard(res.data);
    else toast.error(res.error || 'Erro ao carregar dashboard');
  };

  const handleExportarDashboardCsv = () => {
    if (!formulario || !dashboard) return;
    const linhas: Record<string, string | number>[] = [];
    for (const c of dashboard.campos) {
      if (c.distribuicao && c.distribuicao.length > 0) {
        for (const d of c.distribuicao) {
          linhas.push({ pergunta: c.label, opcao: d.opcao, contagem: d.count, respondidas: c.respondidas, total: dashboard.total_respostas });
        }
      } else {
        linhas.push({
          pergunta: c.label, opcao: '', contagem: '', media: c.media ?? '', respondidas: c.respondidas, total: dashboard.total_respostas,
        });
      }
    }
    const planilha = XLSX.utils.json_to_sheet(linhas);
    const csv = XLSX.utils.sheet_to_csv(planilha);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${formulario.titulo || 'formulario'}-dashboard.csv`.replace(/\s+/g, '_');
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportarRespostasCsv = () => {
    if (!formulario || !respostas) return;
    const linhas = respostas.map((r) => {
      const linha: Record<string, string> = {
        Nome: r.respondente?.nome || r.respondente_nome || '',
        'E-mail': r.respondente?.email || r.respondente_email || '',
        'Enviado em': new Date(r.respondido_em).toLocaleString('pt-BR'),
      };
      for (const campo of campos) {
        const valor = r.valores?.find((v) => v.campo_id === campo.id)?.valor;
        linha[campo.label] = formatarValor(valor);
      }
      return linha;
    });
    const planilha = XLSX.utils.json_to_sheet(linhas);
    const csv = XLSX.utils.sheet_to_csv(planilha);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${formulario.titulo || 'formulario'}-respostas.csv`.replace(/\s+/g, '_');
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleAdicionarCampo = async () => {
    if (!formulario || adicionandoCampo) return;
    setAdicionandoCampo(true);
    const res = await formularioCampoService.create(formulario.id, {
      tipo: 'texto_curto',
      label: '',
      obrigatorio: false,
      configuracao: null,
    });
    setAdicionandoCampo(false);
    if (res.success) {
      setFormulario((prev) => (prev ? { ...prev, campos: [...(prev.campos || []), res.data] } : prev));
      setPendentesDeTipo((prev) => new Set(prev).add(res.data.id));
    } else {
      toast.error(res.error || 'Erro ao adicionar campo');
    }
  };

  const handleEscolherTipo = async (campoId: string, tipo: FormularioCampoTipo) => {
    await handleCampoUpdate(campoId, { tipo });
    setPendentesDeTipo((prev) => {
      const next = new Set(prev);
      next.delete(campoId);
      return next;
    });
  };

  const handleCampoUpdate = async (
    campoId: string,
    patch: Partial<Pick<FormularioCampo, 'tipo' | 'label' | 'obrigatorio' | 'configuracao'>>
  ) => {
    if (!formulario) return;
    if (patch.obrigatorio && formulario.share_token) {
      const campoAtual = (formulario.campos || []).find((c) => c.id === campoId);
      const tipoFinal = patch.tipo ?? campoAtual?.tipo;
      if (tipoFinal && TIPOS_INDISPONIVEIS_LINK_PUBLICO.includes(tipoFinal)) {
        toast.error('Campos de imagem, arquivo ou assinatura não podem ser obrigatórios com o link público ativo — desative o link ou torne o campo opcional.');
        return;
      }
    }
    const res = await formularioCampoService.update(formulario.id, campoId, patch);
    if (res.success) {
      setFormulario((prev) => (prev
        ? { ...prev, campos: (prev.campos || []).map((c) => (c.id === campoId ? res.data : c)) }
        : prev));
      if (res.data.label?.trim()) {
        setCamposInvalidos((prev) => {
          if (!prev.has(campoId)) return prev;
          const next = new Set(prev);
          next.delete(campoId);
          return next;
        });
      }
    } else {
      toast.error(res.error || 'Erro ao salvar campo');
    }
  };

  const handleDuplicarCampo = async (campo: FormularioCampo) => {
    if (!formulario) return;
    const res = await formularioCampoService.create(formulario.id, {
      tipo: campo.tipo,
      label: `${campo.label} (cópia)`,
      obrigatorio: campo.obrigatorio,
      configuracao: campo.configuracao,
    });
    if (res.success) {
      toast.success('Campo duplicado');
      setFormulario((prev) => (prev ? { ...prev, campos: [...(prev.campos || []), res.data] } : prev));
    } else {
      toast.error(res.error || 'Erro ao duplicar campo');
    }
  };

  const handleExcluirCampo = async () => {
    if (!formulario || !excluirAlvo) return;
    const res = await formularioCampoService.delete(formulario.id, excluirAlvo.id);
    if (res.success) {
      toast.success('Campo excluído');
      setFormulario((prev) => (prev
        ? { ...prev, campos: (prev.campos || []).filter((c) => c.id !== excluirAlvo.id) }
        : prev));
    } else {
      toast.error(res.error || 'Erro ao excluir campo');
    }
    setExcluirAlvo(null);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    if (!formulario) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = campos.findIndex((c) => c.id === active.id);
    const newIndex = campos.findIndex((c) => c.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    // Reatribui `ordem` sequencialmente: sem isso, o re-sort por `ordem` no próximo
    // render desfaz o arrayMove visualmente (a ordem antiga dos itens venceria de novo).
    const novaOrdem = arrayMove(campos, oldIndex, newIndex).map((c, i) => ({ ...c, ordem: i }));
    setFormulario((prev) => (prev ? { ...prev, campos: novaOrdem } : prev));

    const res = await formularioCampoService.reordenar(formulario.id, novaOrdem.map((c) => c.id));
    if (!res.success) {
      toast.error(res.error || 'Erro ao reordenar');
      load();
    }
  };

  const handleExportarCsv = () => {
    if (!formulario) return;
    const linhas = campos.map((c) => {
      const cfg = (c.configuracao || {}) as Record<string, unknown>;
      const opcoes = Array.isArray(cfg.opcoes) ? (cfg.opcoes as string[]).join('; ') : '';
      return {
        tipo: c.tipo,
        label: c.label,
        obrigatorio: c.obrigatorio ? 'sim' : 'nao',
        opcoes,
        permite_outro: cfg.permiteOutro ? 'sim' : 'nao',
        escala_min: typeof cfg.min === 'number' ? cfg.min : '',
        escala_max: typeof cfg.max === 'number' ? cfg.max : '',
      };
    });
    const planilha = XLSX.utils.json_to_sheet(linhas, { header: CSV_COLUNAS });
    const csv = XLSX.utils.sheet_to_csv(planilha);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${formulario.titulo || 'formulario'}-perguntas.csv`.replace(/\s+/g, '_');
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImportarCsv = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !formulario) return;

    setImportandoCsv(true);
    try {
      const texto = await file.text();
      const planilha = XLSX.read(texto, { type: 'string' });
      const aba = planilha.Sheets[planilha.SheetNames[0]];
      const linhas = XLSX.utils.sheet_to_json<Record<string, string>>(aba, { defval: '' });

      if (linhas.length === 0) {
        toast.error('CSV vazio ou em formato inesperado. Use "Exportar CSV" pra ver um exemplo do formato esperado.');
        return;
      }

      let importados = 0;
      let ignorados = 0;
      for (const linha of linhas) {
        const tipo = String(linha.tipo || '').trim() as FormularioCampoTipo;
        const label = String(linha.label || '').trim();
        if (!TIPOS_CAMPO_VALORES.includes(tipo)) { ignorados++; continue; }

        const configuracao: Record<string, unknown> = {};
        const opcoesTexto = String(linha.opcoes || '').trim();
        if (opcoesTexto && TIPOS_COM_OPCOES.includes(tipo)) {
          configuracao.opcoes = opcoesTexto.split(';').map((o) => o.trim()).filter(Boolean);
        }
        if (TIPOS_COM_OUTRO.includes(tipo)) {
          configuracao.permiteOutro = String(linha.permite_outro || '').trim().toLowerCase() === 'sim';
        }
        if (tipo === 'escala') {
          const min = parseInt(String(linha.escala_min), 10);
          const max = parseInt(String(linha.escala_max), 10);
          configuracao.min = Number.isNaN(min) ? 1 : min;
          configuracao.max = Number.isNaN(max) ? 5 : max;
        }

        const res = await formularioCampoService.create(formulario.id, {
          tipo,
          label,
          obrigatorio: String(linha.obrigatorio || '').trim().toLowerCase() === 'sim',
          configuracao: Object.keys(configuracao).length ? configuracao : null,
        });
        if (res.success) {
          importados++;
          setFormulario((prev) => (prev ? { ...prev, campos: [...(prev.campos || []), res.data] } : prev));
        } else {
          ignorados++;
        }
      }

      if (importados > 0) {
        toast.success(`${importados} pergunta(s) importada(s)${ignorados ? `, ${ignorados} ignorada(s) por formato inválido` : ''}.`);
      } else {
        toast.error('Nenhuma pergunta importada — confira se a coluna "tipo" usa os valores certos (veja "Exportar CSV" pra um exemplo).');
      }
    } catch {
      toast.error('Não foi possível ler esse arquivo como CSV.');
    } finally {
      setImportandoCsv(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="space-y-4 max-w-4xl mx-auto">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      </AppLayout>
    );
  }

  if (!formulario) {
    return (
      <AppLayout>
        <div className="max-w-4xl mx-auto">Formulário não encontrado.</div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/formularios')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-bold truncate">{formulario.titulo}</h1>
          </div>
          {formulario.status === 'publicado' && (
            <Button variant="outline" size="sm" onClick={() => navigate(`/formularios/${formulario.id}/responder`)}>
              <Send className="h-4 w-4 mr-1.5" /> Responder
            </Button>
          )}
          <Badge className={STATUS_CONFIG[formulario.status].className}>{STATUS_CONFIG[formulario.status].label}</Badge>
        </div>

        <Tabs
          value={abaAtual}
          onValueChange={(v) => {
            setAbaAtual(v);
            if (v === 'respostas') carregarRespostas();
            if (v === 'dashboard') carregarDashboard();
          }}
        >
          <div className="flex items-center justify-between flex-wrap gap-2">
            <TabsList>
              <TabsTrigger value="editor">Editor</TabsTrigger>
              <TabsTrigger value="visualizar">
                <Eye className="h-4 w-4 mr-1.5" /> Prévia
              </TabsTrigger>
              <TabsTrigger value="respostas">Ver respostas</TabsTrigger>
              <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
            </TabsList>
            {abaAtual === 'visualizar' && (
              <Badge variant="secondary">Prévia — nada aqui é salvo de verdade</Badge>
            )}
          </div>

          <TabsContent value="editor" className="mt-4">
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6 items-start">
              {/* Coluna principal — Campos */}
              <div className="space-y-4 min-w-0">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <h2 className="text-lg font-semibold flex items-center gap-2">
                    <ListChecks className="h-5 w-5" /> Campos
                  </h2>
                  {isAdmin && (
                    <div className="flex items-center gap-2">
                      <input ref={csvInputRef} type="file" accept=".csv,text/csv" className="hidden" onChange={handleImportarCsv} />
                      <Button variant="outline" size="sm" onClick={() => csvInputRef.current?.click()} disabled={importandoCsv}>
                        {importandoCsv ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}
                        Importar CSV
                      </Button>
                      <Button variant="outline" size="sm" onClick={handleExportarCsv} disabled={campos.length === 0}>
                        <Download className="h-4 w-4 mr-1.5" /> Exportar CSV
                      </Button>
                      <Button size="sm" onClick={handleAdicionarCampo} disabled={adicionandoCampo}>
                        {adicionandoCampo ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
                        Adicionar campo
                      </Button>
                    </div>
                  )}
                </div>

                {campos.length === 0 ? (
                  <Card>
                    <CardContent className="py-10 text-center text-muted-foreground">
                      Nenhum campo ainda.
                    </CardContent>
                  </Card>
                ) : (
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={campos.map((c) => c.id)} strategy={verticalListSortingStrategy}>
                      <div className="space-y-2">
                        {campos.map((campo) => (
                          <CampoRow
                            key={campo.id}
                            campo={campo}
                            isAdmin={isAdmin}
                            precisaEscolherTipo={pendentesDeTipo.has(campo.id)}
                            semTexto={camposInvalidos.has(campo.id)}
                            onUpdate={(patch) => handleCampoUpdate(campo.id, patch)}
                            onEscolherTipo={(tipo) => handleEscolherTipo(campo.id, tipo)}
                            onDelete={setExcluirAlvo}
                            onDuplicar={handleDuplicarCampo}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                )}

                {isAdmin && campos.length > 0 && (
                  <Button variant="outline" size="sm" onClick={handleAdicionarCampo} disabled={adicionandoCampo}>
                    {adicionandoCampo ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
                    Adicionar campo
                  </Button>
                )}
              </div>

              {/* Barra lateral — configurações do formulário */}
              {isAdmin && (
                <Card className="lg:sticky lg:top-6">
                  <CardContent className="pt-6 space-y-4">
                    <div className="space-y-2">
                      <Label>Título</Label>
                      <Input
                        value={formulario.titulo}
                        onChange={(e) => setFormulario({ ...formulario, titulo: e.target.value })}
                        onBlur={(e) => atualizarFormulario({ titulo: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Status</Label>
                      <Select
                        value={formulario.status}
                        onValueChange={(v) => handleStatusChange(v as FormularioStatus)}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="rascunho">Rascunho</SelectItem>
                          <SelectItem value="publicado">Publicado</SelectItem>
                          <SelectItem value="arquivado">Arquivado</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Descrição</Label>
                      <Textarea
                        value={formulario.descricao || ''}
                        onChange={(e) => setFormulario({ ...formulario, descricao: e.target.value })}
                        onBlur={(e) => atualizarFormulario({ descricao: e.target.value })}
                        rows={2}
                      />
                    </div>
                    <div className="space-y-3 pt-2 border-t">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={formulario.permite_multiplas_respostas_por_pessoa}
                          onCheckedChange={(v) => atualizarFormulario({ permite_multiplas_respostas_por_pessoa: v })}
                          disabled={salvandoConfig}
                        />
                        <Label className="cursor-pointer">Permite múltiplas respostas por pessoa</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={formulario.permite_edicao_pelo_respondente}
                          onCheckedChange={(v) => atualizarFormulario({ permite_edicao_pelo_respondente: v })}
                          disabled={salvandoConfig}
                        />
                        <Label className="cursor-pointer">Quem responde pode editar depois</Label>
                      </div>
                    </div>
                    <div className="space-y-3 pt-2 border-t">
                      <Label className="flex items-center gap-1.5 font-medium">
                        <Share2 className="h-3.5 w-3.5" />
                        Compartilhar link
                      </Label>

                      {formulario.share_token && formulario.status !== 'publicado' && (
                        <p className="text-xs text-destructive bg-destructive/10 border border-destructive/30 rounded-md p-2">
                          Este formulário está como "{STATUS_CONFIG[formulario.status].label}" — enquanto não voltar para
                          "Publicado", os links acima não vão funcionar para quem não é admin.
                        </p>
                      )}

                      <div className="flex items-center gap-2">
                        <Switch
                          checked={!!formulario.share_token}
                          onCheckedChange={handleAlternarLinkPublico}
                          disabled={gerandoLink}
                        />
                        <Label className="cursor-pointer">
                          Converter para link público
                        </Label>
                      </div>

                      <div className="space-y-1.5">
                        <p className="text-xs text-muted-foreground">
                          {formulario.share_token
                            ? 'Qualquer pessoa com o link consegue acessar e responder, sem precisar de login.'
                            : 'Para quem já tem login no sistema'}
                        </p>
                        <div className="flex flex-col gap-2">
                          <Input
                            readOnly
                            value={formulario.share_token
                              ? `${window.location.origin}/fp/${formulario.share_token}`
                              : `${window.location.origin}/formularios/${formulario.id}/responder`}
                            className="text-sm"
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => copiarLinkPublico(formulario.share_token
                              ? `${window.location.origin}/fp/${formulario.share_token}`
                              : `${window.location.origin}/formularios/${formulario.id}/responder`)}
                          >
                            <Copy className="h-3.5 w-3.5 mr-1.5" />
                            Copiar link
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          <TabsContent value="visualizar">
            <FormularioPreview formulario={formulario} />
          </TabsContent>

          <TabsContent value="respostas" className="space-y-4">
            {carregandoRespostas ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
              </div>
            ) : !respostas || respostas.length === 0 ? (
              <Card>
                <CardContent className="py-10 text-center text-muted-foreground">
                  Nenhuma resposta recebida ainda.
                </CardContent>
              </Card>
            ) : (
              <>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center rounded-md border bg-muted/50 p-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Buscar por nome ou e-mail..."
                      value={buscaResposta}
                      onChange={(e) => setBuscaResposta(e.target.value)}
                      className="pl-9 bg-background"
                    />
                  </div>
                  {(buscaResposta || filtroCampoId) && (
                    <Button variant="ghost" size="icon" onClick={() => { setBuscaResposta(''); setFiltroCampoId(''); setFiltroValor(''); }} title="Limpar filtros">
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm">
                        <Columns3 className="h-4 w-4 mr-1.5" /> Colunas
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="max-h-72 overflow-y-auto">
                      <DropdownMenuLabel>Perguntas visíveis</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      {campos.map((c) => (
                        <DropdownMenuCheckboxItem
                          key={c.id}
                          checked={!colunasOcultas.has(c.id)}
                          onCheckedChange={(checked) => {
                            setColunasOcultas((prev) => {
                              const next = new Set(prev);
                              if (checked) next.delete(c.id); else next.add(c.id);
                              return next;
                            });
                          }}
                        >
                          {c.label}
                        </DropdownMenuCheckboxItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Button variant="outline" size="sm" onClick={handleExportarRespostasCsv}>
                    <Download className="h-4 w-4 mr-1.5" /> Exportar CSV
                  </Button>
                </div>

                {camposFiltraveis.length > 0 && (
                  <Card>
                    <CardContent className="py-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:flex-wrap">
                      <Select value={filtroCampoId || 'nenhum'} onValueChange={(v) => { setFiltroCampoId(v === 'nenhum' ? '' : v); setFiltroValor(''); }}>
                        <SelectTrigger className="sm:w-56"><SelectValue placeholder="Filtrar por pergunta" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="nenhum">Filtrar por pergunta...</SelectItem>
                          {camposFiltraveis.map((c) => (
                            <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {campoDoFiltro && (
                        <Select value={filtroValor || 'nenhum'} onValueChange={(v) => setFiltroValor(v === 'nenhum' ? '' : v)}>
                          <SelectTrigger className="sm:w-48"><SelectValue placeholder="Valor..." /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="nenhum">Qualquer resposta</SelectItem>
                            {opcoesParaFiltro(campoDoFiltro).map((o) => (
                              <SelectItem key={o} value={o}>{o}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </CardContent>
                  </Card>
                )}

                <Card>
                  <CardContent className="p-0 overflow-x-auto">
                    <Table
                      style={{
                        tableLayout: 'fixed',
                        width: `max(100%, ${
                          larguraColuna('nome', 200) +
                          larguraColuna('email', 200) +
                          larguraColuna('data', 180) +
                          colunasRespostas.reduce((soma, c) => soma + larguraColuna(c.id, 160), 0)
                        }px)`,
                      }}
                    >
                      <colgroup>
                        <col style={{ width: larguraColuna('nome', 200) }} />
                        <col style={{ width: larguraColuna('email', 200) }} />
                        <col style={{ width: larguraColuna('data', 180) }} />
                        {colunasRespostas.map((c) => (
                          <col key={c.id} style={{ width: larguraColuna(c.id, 160) }} />
                        ))}
                      </colgroup>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="sticky left-0 bg-card relative pr-3">
                            <SortableHeader coluna="nome" ordenacao={ordenacaoResposta} onClick={toggleOrdenacaoResposta}>Nome</SortableHeader>
                            <div
                              className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize select-none hover:bg-primary/40"
                              onMouseDown={(e) => iniciarRedimensionamento(e, 'nome', larguraColuna('nome', 200))}
                            />
                          </TableHead>
                          <TableHead className="relative pr-3">
                            <SortableHeader coluna="email" ordenacao={ordenacaoResposta} onClick={toggleOrdenacaoResposta}>E-mail</SortableHeader>
                            <div
                              className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize select-none hover:bg-primary/40"
                              onMouseDown={(e) => iniciarRedimensionamento(e, 'email', larguraColuna('email', 200))}
                            />
                          </TableHead>
                          <TableHead className="relative pr-3">
                            <SortableHeader coluna="data" ordenacao={ordenacaoResposta} onClick={toggleOrdenacaoResposta}>Enviado em</SortableHeader>
                            <div
                              className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize select-none hover:bg-primary/40"
                              onMouseDown={(e) => iniciarRedimensionamento(e, 'data', larguraColuna('data', 180))}
                            />
                          </TableHead>
                          {colunasRespostas.map((c) => (
                            <TableHead key={c.id} className="relative pr-3">
                              <SortableHeader coluna={c.id} ordenacao={ordenacaoResposta} onClick={toggleOrdenacaoResposta}>{c.label}</SortableHeader>
                              <div
                                className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize select-none hover:bg-primary/40"
                                onMouseDown={(e) => iniciarRedimensionamento(e, c.id, larguraColuna(c.id, 160))}
                              />
                            </TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {respostasFiltradas.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={3 + colunasRespostas.length} className="text-center text-muted-foreground py-8">
                              Nenhuma resposta encontrada para os filtros aplicados.
                            </TableCell>
                          </TableRow>
                        ) : (
                          respostasFiltradas.map((r) => (
                            <TableRow key={r.id} className="cursor-pointer" onClick={() => setRespostaDetalhe(r)}>
                              <TableCell className="sticky left-0 bg-card font-medium">{r.respondente?.nome || r.respondente_nome || '—'}</TableCell>
                              <TableCell>{r.respondente?.email || r.respondente_email || '—'}</TableCell>
                              <TableCell className="whitespace-nowrap">{new Date(r.respondido_em).toLocaleString('pt-BR')}</TableCell>
                              {colunasRespostas.map((c) => (
                                <TableCell key={c.id} className="max-w-[240px] truncate">
                                  {formatarValor(r.valores?.find((v) => v.campo_id === c.id)?.valor)}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          <TabsContent value="dashboard" className="space-y-4">
            {carregandoDashboard ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => <Skeleton key={i} className="h-24 w-full" />)}
              </div>
            ) : !dashboard || dashboard.total_respostas === 0 ? (
              <Card>
                <CardContent className="py-10 text-center text-muted-foreground">
                  Nenhuma resposta recebida ainda.
                </CardContent>
              </Card>
            ) : (
              <>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <h2 className="text-lg font-semibold">Dashboard</h2>
                  <Button variant="outline" size="sm" onClick={handleExportarDashboardCsv}>
                    <Download className="h-4 w-4 mr-1.5" /> Exportar CSV
                  </Button>
                </div>

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="pt-6">
                      <p className="text-xs text-muted-foreground">Total de respostas</p>
                      <p className="text-2xl font-bold">{dashboard.total_respostas}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <p className="text-xs text-muted-foreground">Taxa média de conclusão</p>
                      <p className="text-2xl font-bold">
                        {dashboard.campos.length > 0
                          ? Math.round(
                              (dashboard.campos.reduce((acc, c) => acc + c.respondidas / (dashboard.total_respostas || 1), 0) / dashboard.campos.length) * 100
                            )
                          : 0}%
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <p className="text-xs text-muted-foreground">Primeira resposta</p>
                      <p className="text-lg font-semibold">
                        {dashboard.respostas_por_dia[0]
                          ? new Date(`${dashboard.respostas_por_dia[0].data}T00:00:00`).toLocaleDateString('pt-BR')
                          : '—'}
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <p className="text-xs text-muted-foreground">Última resposta</p>
                      <p className="text-lg font-semibold">
                        {dashboard.respostas_por_dia.length > 0
                          ? new Date(`${dashboard.respostas_por_dia[dashboard.respostas_por_dia.length - 1].data}T00:00:00`).toLocaleDateString('pt-BR')
                          : '—'}
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {(() => {
                  const hoje = new Date();
                  const respostasPeriodo =
                    periodoDashboard === 'tudo'
                      ? dashboard.respostas_por_dia
                      : dashboard.respostas_por_dia.filter((d) => {
                          const dias = (hoje.getTime() - new Date(`${d.data}T00:00:00`).getTime()) / 86400000;
                          return dias <= Number(periodoDashboard);
                        });
                  if (dashboard.respostas_por_dia.length <= 1) return null;
                  return (
                    <Card>
                      <CardContent className="pt-6 space-y-2">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <p className="font-medium text-sm">Respostas ao longo do tempo</p>
                          <div className="flex gap-1">
                            {(['7', '30', '90', 'tudo'] as const).map((p) => (
                              <Button
                                key={p}
                                variant={periodoDashboard === p ? 'default' : 'outline'}
                                size="sm"
                                className="h-7 px-2 text-xs"
                                onClick={() => setPeriodoDashboard(p)}
                              >
                                {p === 'tudo' ? 'Tudo' : `${p}d`}
                              </Button>
                            ))}
                          </div>
                        </div>
                        {respostasPeriodo.length === 0 ? (
                          <p className="text-sm text-muted-foreground py-4 text-center">Nenhuma resposta nesse período.</p>
                        ) : (
                          <div className="flex items-end gap-1 h-32">
                            {respostasPeriodo.map((d) => {
                              const max = Math.max(...respostasPeriodo.map((x) => x.count), 1);
                              return (
                                <div key={d.data} className="flex-1 flex flex-col items-center justify-end gap-1 h-full" title={`${d.data}: ${d.count}`}>
                                  <span className="text-[10px] text-muted-foreground">{d.count}</span>
                                  <div className="w-full bg-primary rounded-sm" style={{ height: `${(d.count / max) * 100}%`, minHeight: 2 }} />
                                  <span className="text-[9px] text-muted-foreground">{d.data.slice(5)}</span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })()}

                <p className="text-xs text-muted-foreground">Perguntas com mais respostas pendentes ou negativas aparecem primeiro.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {[...dashboard.campos]
                    .sort((a, b) => criticidadeCampo(b, dashboard.total_respostas) - criticidadeCampo(a, dashboard.total_respostas))
                    .map((c) => {
                    const taxa = dashboard.total_respostas > 0 ? c.respondidas / dashboard.total_respostas : 0;
                    const corTaxa = taxa >= 0.8 ? 'text-emerald-700 bg-emerald-100' : taxa >= 0.5 ? 'text-amber-700 bg-amber-100' : 'text-rose-700 bg-rose-100';
                    const tipoGrafico = tipoGraficoPorCampo[c.campo_id] || 'barra';
                    return (
                      <Card key={c.campo_id}>
                        <CardContent className="pt-6 space-y-3">
                          <div className="flex items-center justify-between gap-2">
                            <p className="font-medium">{c.label}</p>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${corTaxa}`}>
                              {c.respondidas} de {dashboard.total_respostas} ({Math.round(taxa * 100)}%)
                            </span>
                          </div>
                          {c.distribuicao && c.distribuicao.length > 0 && (
                            <div className="flex justify-end gap-1">
                              <Button
                                variant={tipoGrafico === 'barra' ? 'secondary' : 'ghost'}
                                size="icon"
                                className="h-6 w-6"
                                title="Barra"
                                onClick={() => setTipoGraficoPorCampo((prev) => ({ ...prev, [c.campo_id]: 'barra' }))}
                              >
                                <BarChart3 className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                variant={tipoGrafico === 'pizza' ? 'secondary' : 'ghost'}
                                size="icon"
                                className="h-6 w-6"
                                title="Pizza"
                                onClick={() => setTipoGraficoPorCampo((prev) => ({ ...prev, [c.campo_id]: 'pizza' }))}
                              >
                                <PieChart className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          )}
                          {c.distribuicao && c.distribuicao.length > 0 && (
                            tipoGrafico === 'pizza' ? (
                              <DonutDistribuicao dados={[...c.distribuicao].sort((a, b) => b.count - a.count)} />
                            ) : (
                            <div className="space-y-1.5">
                              {c.distribuicao
                                .sort((a, b) => b.count - a.count)
                                .map((d) => {
                                  const max = Math.max(...(c.distribuicao || []).map((x) => x.count), 1);
                                  const pct = c.respondidas > 0 ? Math.round((d.count / c.respondidas) * 100) : 0;
                                  return (
                                    <div key={d.opcao} className="space-y-0.5">
                                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                                        <span className="truncate">{d.opcao}</span>
                                        <span>{d.count} ({pct}%)</span>
                                      </div>
                                      <div className="h-2 rounded-full bg-muted overflow-hidden">
                                        <div className="h-full bg-primary rounded-full" style={{ width: `${(d.count / max) * 100}%` }} />
                                      </div>
                                    </div>
                                  );
                                })}
                            </div>
                            )
                          )}
                          {typeof c.media === 'number' && (
                            <p className="text-sm">
                              Média: <span className="font-medium">{c.media.toFixed(1)}</span>
                              {typeof c.minimo === 'number' && typeof c.maximo === 'number' && (
                                <span className="text-muted-foreground"> (mín {c.minimo}, máx {c.maximo})</span>
                              )}
                            </p>
                          )}
                          {c.amostras && c.amostras.length > 0 && (
                            <div className="space-y-1 pt-1 border-t">
                              <p className="text-xs text-muted-foreground">Algumas respostas:</p>
                              {c.amostras.map((a, i) => (
                                <p key={i} className="text-sm truncate">"{a}"</p>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={!!respostaDetalhe} onOpenChange={(open) => !open && setRespostaDetalhe(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Resposta de {respostaDetalhe?.respondente?.nome || respostaDetalhe?.respondente_nome || '...'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              {respostaDetalhe?.respondente?.email || respostaDetalhe?.respondente_email} · {respostaDetalhe && new Date(respostaDetalhe.respondido_em).toLocaleString('pt-BR')}
            </p>
            {(respostaDetalhe?.valores || []).map((v) => (
              <div key={v.id} className="space-y-0.5">
                <p className="text-xs font-medium text-muted-foreground">{v.campo?.label || 'Campo removido'}</p>
                <p className="text-sm">{formatarValor(v.valor)}</p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!excluirAlvo} onOpenChange={(open) => !open && setExcluirAlvo(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir campo?</AlertDialogTitle>
            <AlertDialogDescription>
              Isso vai excluir "{excluirAlvo?.label}" e todas as respostas já dadas a esse campo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleExcluirCampo} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmarPublicarLink} onOpenChange={(open) => !open && setConfirmarPublicarLink(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Publicar formulário para gerar o link?</AlertDialogTitle>
            <AlertDialogDescription>
              Este formulário está como "{formulario ? STATUS_CONFIG[formulario.status].label : ''}". Para o link
              público funcionar, o status precisa ser "Publicado". Quer mudar o status agora e gerar o link?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmarPublicarEGerarLink}>
              Publicar e gerar link
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmarDesativarLink} onOpenChange={(open) => !open && setConfirmarDesativarLink(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Desativar o link público?</AlertDialogTitle>
            <AlertDialogDescription>
              O link atual para de funcionar na hora — quem já recebeu esse link (mesmo quem você já mandou) vai ver
              "Link inválido" ao tentar abrir. Se você ativar de novo depois, um link novo e diferente é gerado; o
              antigo não volta a funcionar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmarDesativarLink} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Desativar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!confirmarStatusPendente} onOpenChange={(open) => !open && setConfirmarStatusPendente(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Isso vai desativar o link público</AlertDialogTitle>
            <AlertDialogDescription>
              Este formulário tem um link público ativo. Enquanto o status não voltar para "Publicado", quem tiver
              esse link vai ver "Link inválido" ao tentar abrir. Quer mudar o status mesmo assim?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmarTrocarStatus} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Mudar status mesmo assim
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
